import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Loader2, Upload, Send, FileSpreadsheet, Trash2, Plus, Users,
  AlertCircle, CheckCircle2, ArrowLeft, Mail, Eye, X,
} from "lucide-react";
import { getToken, formatApiError } from "@/lib/adminApi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHdr = () => ({ Authorization: `Bearer ${getToken()}` });

const CONSENT_OPTIONS = [
  { value: "consent", label: "Consenso esplicito (le persone hanno acconsentito a ricevere comunicazioni)" },
  { value: "legitimate_interest", label: "Interesse legittimo (es. business contacts del settore, networking professionale)" },
  { value: "contract", label: "Contratto / rapporto pre-esistente (clienti, partner)" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function OutreachTab() {
  const [view, setView] = useState("home"); // home | import | contacts | new-campaign | campaign-detail
  const [campaignId, setCampaignId] = useState(null);

  if (view === "import") return <ImportView onBack={() => setView("contacts")} onDone={() => setView("contacts")} />;
  if (view === "contacts") return <ContactsView onBack={() => setView("home")} />;
  if (view === "new-campaign") return <CampaignEditor onBack={() => setView("home")} onCreated={(id) => { setCampaignId(id); setView("campaign-detail"); }} />;
  if (view === "campaign-detail") return <CampaignDetail cid={campaignId} onBack={() => setView("home")} />;
  return <HomeView onNavigate={setView} />;
}

// ============================================================
// HOME
// ============================================================

function HomeView({ onNavigate }) {
  const [stats, setStats] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [c, cs] = await Promise.all([
          axios.get(`${API}/admin/outreach/contacts?limit=1`, { headers: authHdr() }),
          axios.get(`${API}/admin/outreach/campaigns`, { headers: authHdr() }),
        ]);
        setStats({ total: c.data.total, ...c.data.stats });
        setCampaigns(cs.data.items || []);
      } catch {
        /* silent */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div data-testid="admin-outreach-tab" className="space-y-8">
      <div className="bg-gradient-to-br from-[#0B192C] via-[#173F5F] to-[#0E7C8F] text-[#F9F8F6] p-8 md:p-10">
        <p className="overline text-[#E89B6C]">Outreach Email</p>
        <h2 className="font-serif-display text-3xl md:text-4xl mt-2 leading-tight">
          Diffondi il progetto Metodo di Mare
        </h2>
        <p className="mt-4 text-sm font-light text-[#F9F8F6]/80 max-w-2xl">
          Carica un file Excel/CSV con email e nomi, prepara una campagna e invia
          un messaggio personalizzato per far conoscere il libro, i corsi o l&apos;associazione.
        </p>

        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          <Stat label="Contatti totali" value={stats?.total ?? "—"} testid="outreach-stat-total" />
          <Stat label="Attivi" value={stats?.active ?? 0} accent testid="outreach-stat-active" />
          <Stat label="Disiscritti" value={stats?.unsubscribed ?? 0} testid="outreach-stat-unsub" />
          <Stat label="Bounce" value={stats?.bounced ?? 0} testid="outreach-stat-bounce" />
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <button
            onClick={() => onNavigate("import")}
            data-testid="outreach-import-btn"
            className="inline-flex items-center gap-2 px-5 py-3 bg-[#E89B6C] text-[#0B192C] text-xs uppercase tracking-[0.2em] hover:bg-[#F9F8F6] transition-colors"
          >
            <Upload size={13} /> Importa contatti
          </button>
          <button
            onClick={() => onNavigate("contacts")}
            data-testid="outreach-contacts-btn"
            className="inline-flex items-center gap-2 px-5 py-3 border border-[#F9F8F6]/30 text-[#F9F8F6] text-xs uppercase tracking-[0.2em] hover:bg-[#F9F8F6]/10"
          >
            <Users size={13} /> Vedi contatti
          </button>
          <button
            onClick={() => onNavigate("new-campaign")}
            data-testid="outreach-new-campaign-btn"
            disabled={!stats?.active}
            className="inline-flex items-center gap-2 px-5 py-3 border border-[#E89B6C]/60 text-[#E89B6C] text-xs uppercase tracking-[0.2em] hover:bg-[#E89B6C] hover:text-[#0B192C] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Send size={13} /> Nuova campagna
          </button>
        </div>
      </div>

      <div>
        <h3 className="font-serif-display text-2xl text-[#0B192C] mb-4">Campagne recenti</h3>
        {loading ? (
          <div className="flex items-center gap-3 text-[#3A4F6B]"><Loader2 className="animate-spin" size={16} /> Caricamento...</div>
        ) : campaigns.length === 0 ? (
          <div className="border border-dashed border-[#E5E1DA] py-14 text-center text-sm text-[#3A4F6B]">
            Nessuna campagna ancora. Importa contatti e crea la prima.
          </div>
        ) : (
          <div className="border border-[#E5E1DA]">
            <table className="w-full text-sm">
              <thead className="bg-[#F4E8D6]/40 text-[10px] uppercase tracking-[0.18em] text-[#3A4F6B]">
                <tr>
                  <th className="text-left px-4 py-3 font-normal">Nome</th>
                  <th className="text-left px-4 py-3 font-normal">Oggetto</th>
                  <th className="text-left px-4 py-3 font-normal">Audience</th>
                  <th className="text-left px-4 py-3 font-normal">Stato</th>
                  <th className="text-left px-4 py-3 font-normal">Invii</th>
                  <th className="text-left px-4 py-3 font-normal">Data</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E1DA]">
                {campaigns.map((c) => (
                  <tr key={c.id} className="hover:bg-[#F9F8F6]" data-testid={`outreach-campaign-row-${c.id}`}>
                    <td className="px-4 py-3 font-medium">{c.name}</td>
                    <td className="px-4 py-3 text-xs">{c.subject}</td>
                    <td className="px-4 py-3 text-xs">
                      {c.audience_count} · <span className="text-[#3A4F6B]">{c.list_label || "tutti"}</span>
                    </td>
                    <td className="px-4 py-3"><CampaignStatus status={c.status} /></td>
                    <td className="px-4 py-3 text-xs">
                      {c.status === "sent" ? `${c.success_count}/${c.audience_count}` : c.status === "sending" ? "in corso..." : "—"}
                    </td>
                    <td className="px-4 py-3 text-xs text-[#3A4F6B]">{fmtDate(c.sent_at || c.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, accent, testid }) {
  return (
    <div data-testid={testid} className={`${accent ? "bg-[#E89B6C]/20 border-[#E89B6C]/40" : "bg-[#F9F8F6]/8 border-[#F9F8F6]/15"} border backdrop-blur-md p-3 md:p-4`}>
      <p className="overline text-[#F9F8F6]/70">{label}</p>
      <p className="font-serif-display text-2xl md:text-3xl mt-1 text-[#F9F8F6]">{value}</p>
    </div>
  );
}

function CampaignStatus({ status }) {
  const map = {
    draft: { label: "Bozza", cls: "bg-[#E5E1DA] text-[#0B192C]" },
    sending: { label: "Invio...", cls: "bg-[#E89B6C] text-[#0B192C]" },
    sent: { label: "Inviata", cls: "bg-[#0E7C8F] text-[#F9F8F6]" },
  };
  const s = map[status] || { label: status, cls: "bg-[#E5E1DA] text-[#0B192C]" };
  return <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] ${s.cls}`}>{s.label}</span>;
}

// ============================================================
// IMPORT
// ============================================================

function ImportView({ onBack, onDone }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [listLabel, setListLabel] = useState("");
  const [consentBasis, setConsentBasis] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!file || !listLabel.trim() || !consentBasis || !acknowledged) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("list_label", listLabel.trim());
      fd.append("consent_basis", consentBasis);
      const { data } = await axios.post(`${API}/admin/outreach/import`, fd, {
        headers: { ...authHdr(), "Content-Type": "multipart/form-data" },
      });
      setResult(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="outreach-import-view" className="max-w-2xl">
      <button onClick={onBack} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#0B192C] hover:text-[#E89B6C] mb-6">
        <ArrowLeft size={13} /> Indietro
      </button>

      <h2 className="font-serif-display text-3xl text-[#0B192C]">Importa contatti</h2>
      <p className="mt-2 text-sm text-[#3A4F6B] font-light">
        Carica un file <code className="font-mono">.xlsx</code> o <code className="font-mono">.csv</code> con
        intestazione di colonne. La colonna <strong>email</strong> è obbligatoria.
        Riconosco anche: <code className="font-mono">nome</code>, <code className="font-mono">cognome</code>, <code className="font-mono">azienda</code>, <code className="font-mono">telefono</code>, <code className="font-mono">note</code>.
      </p>

      {result ? (
        <div data-testid="outreach-import-result" className="mt-6 bg-[#0E7C8F]/10 border border-[#0E7C8F] p-6">
          <div className="flex items-start gap-3">
            <CheckCircle2 size={24} className="text-[#0E7C8F] flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-serif-display text-xl text-[#0B192C]">Importazione completata</h3>
              <ul className="mt-4 space-y-1 text-sm font-light text-[#0B192C]">
                <li>Righe nel file: <strong>{result.rows_in_file}</strong></li>
                <li>Email valide: <strong>{result.valid_emails}</strong></li>
                <li>Già presenti nel DB: <strong>{result.already_in_db}</strong></li>
                <li className="text-[#0E7C8F]">Nuovi contatti aggiunti: <strong>{result.inserted}</strong></li>
                <li className="text-[#3A4F6B]">Saltate (email invalide o duplicate): <strong>{result.skipped_invalid_or_duplicate}</strong></li>
              </ul>
              <button onClick={onDone} className="mt-6 inline-flex items-center gap-2 px-5 py-3 bg-[#0B192C] text-[#F9F8F6] text-xs uppercase tracking-[0.18em] hover:bg-[#E89B6C] hover:text-[#0B192C]">
                Vedi contatti importati <ArrowLeft size={13} className="rotate-180" />
              </button>
            </div>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-6 space-y-6 bg-white border border-[#E5E1DA] p-6 md:p-8">
          <div>
            <label className="overline block mb-2">File (.xlsx o .csv) *</label>
            <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-2 px-4 py-3 border border-[#0B192C] text-xs uppercase tracking-[0.18em] hover:bg-[#0B192C] hover:text-[#F9F8F6] cursor-pointer">
                <FileSpreadsheet size={13} /> Scegli file
                <input
                  ref={fileRef}
                  type="file"
                  accept=".xlsx,.csv"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  data-testid="outreach-file-input"
                  className="hidden"
                />
              </label>
              {file && <span className="text-xs font-mono text-[#3A4F6B] truncate">{file.name} · {Math.round(file.size / 1024)} KB</span>}
            </div>
          </div>

          <div>
            <label className="overline block mb-2">Nome lista *</label>
            <input
              type="text"
              required
              value={listLabel}
              onChange={(e) => setListLabel(e.target.value)}
              placeholder="es. 'Contatti fiera Milano 2026'"
              data-testid="outreach-list-label-input"
              className="w-full px-3 py-2 border border-[#0B192C]/20 bg-white text-sm focus:outline-none focus:border-[#0B192C]"
            />
            <p className="text-[10px] text-[#3A4F6B] mt-1">Usato per filtrare i contatti quando crei una campagna</p>
          </div>

          <div>
            <label className="overline block mb-2">Base legale (GDPR) *</label>
            <div className="space-y-2">
              {CONSENT_OPTIONS.map((o) => (
                <label key={o.value} className={`flex items-start gap-3 p-3 border cursor-pointer transition-colors ${consentBasis === o.value ? "border-[#E89B6C] bg-[#F4E8D6]/40" : "border-[#E5E1DA] hover:border-[#0B192C]/40"}`}>
                  <input
                    type="radio"
                    name="consent_basis"
                    value={o.value}
                    checked={consentBasis === o.value}
                    onChange={() => setConsentBasis(o.value)}
                    data-testid={`outreach-consent-${o.value}`}
                    className="mt-1"
                  />
                  <span className="text-sm font-light text-[#0B192C]">{o.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="bg-[#F4E8D6]/40 border-l-4 border-[#E89B6C] p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                data-testid="outreach-gdpr-ack"
                className="mt-1 flex-shrink-0"
              />
              <span className="text-xs font-light text-[#0B192C] leading-relaxed">
                <strong>Confermo</strong> di avere una base legale valida ai sensi del GDPR (Reg. UE 2016/679)
                per contattare le persone presenti nel file. Mi assumo la responsabilità della liceità del trattamento.
                Ogni email includerà un link di disiscrizione con 1 click.
              </span>
            </label>
          </div>

          {error && (
            <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 p-3">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={!file || !listLabel.trim() || !consentBasis || !acknowledged || loading}
            data-testid="outreach-import-submit"
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-4 bg-[#0B192C] text-[#F9F8F6] text-xs uppercase tracking-[0.2em] hover:bg-[#E89B6C] hover:text-[#0B192C] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? <><Loader2 size={13} className="animate-spin" /> Importazione...</> : <><Upload size={13} /> Importa contatti</>}
          </button>
        </form>
      )}
    </div>
  );
}

// ============================================================
// CONTACTS LIST
// ============================================================

function ContactsView({ onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filterLabel, setFilterLabel] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (filterLabel) params.set("list_label", filterLabel);
      if (filterStatus) params.set("status", filterStatus);
      const { data } = await axios.get(`${API}/admin/outreach/contacts?${params}`, { headers: authHdr() });
      setData(data);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, filterLabel, filterStatus]);

  const remove = async (id) => {
    if (!window.confirm("Eliminare definitivamente questo contatto?")) return;
    try {
      await axios.delete(`${API}/admin/outreach/contacts/${id}`, { headers: authHdr() });
      load();
    } catch (err) { alert(formatApiError(err)); }
  };

  return (
    <div data-testid="outreach-contacts-view">
      <button onClick={onBack} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#0B192C] hover:text-[#E89B6C] mb-6">
        <ArrowLeft size={13} /> Indietro
      </button>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <h2 className="font-serif-display text-3xl text-[#0B192C]">Contatti outreach</h2>
        <p className="text-xs uppercase tracking-[0.18em] text-[#3A4F6B]">{data?.total ?? 0} contatti</p>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <input
          type="text"
          placeholder="Cerca per email, nome, azienda..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="outreach-contacts-search"
          className="flex-1 min-w-[220px] px-3 py-2 border border-[#0B192C]/20 bg-white text-sm focus:outline-none focus:border-[#0B192C]"
        />
        <select
          value={filterLabel}
          onChange={(e) => setFilterLabel(e.target.value)}
          data-testid="outreach-filter-label"
          className="px-3 py-2 border border-[#0B192C]/20 bg-white text-sm focus:outline-none focus:border-[#0B192C]"
        >
          <option value="">Tutte le liste</option>
          {(data?.labels || []).map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          data-testid="outreach-filter-status"
          className="px-3 py-2 border border-[#0B192C]/20 bg-white text-sm focus:outline-none focus:border-[#0B192C]"
        >
          <option value="">Tutti gli stati</option>
          <option value="active">Attivi</option>
          <option value="unsubscribed">Disiscritti</option>
          <option value="bounced">Bounce</option>
        </select>
      </div>

      {loading ? (
        <div className="flex items-center gap-3 text-[#3A4F6B]"><Loader2 className="animate-spin" size={16} /> Caricamento...</div>
      ) : !data?.items?.length ? (
        <div className="border border-dashed border-[#E5E1DA] py-14 text-center text-sm text-[#3A4F6B]">Nessun contatto.</div>
      ) : (
        <div className="border border-[#E5E1DA] overflow-x-auto">
          <table className="w-full text-sm" data-testid="outreach-contacts-table">
            <thead className="bg-[#F4E8D6]/40 text-[10px] uppercase tracking-[0.18em] text-[#3A4F6B]">
              <tr>
                <th className="text-left px-4 py-3 font-normal">Email</th>
                <th className="text-left px-4 py-3 font-normal">Nome</th>
                <th className="text-left px-4 py-3 font-normal">Azienda</th>
                <th className="text-left px-4 py-3 font-normal">Lista</th>
                <th className="text-left px-4 py-3 font-normal">Stato</th>
                <th className="text-left px-4 py-3 font-normal">Importato</th>
                <th></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E1DA]">
              {data.items.map((c) => (
                <tr key={c.id} className="hover:bg-[#F9F8F6]">
                  <td className="px-4 py-3 font-mono text-xs">{c.email}</td>
                  <td className="px-4 py-3">{c.name || "—"}</td>
                  <td className="px-4 py-3 text-xs">{c.company || "—"}</td>
                  <td className="px-4 py-3 text-xs">{c.list_label}</td>
                  <td className="px-4 py-3">
                    {c.status === "active" && <span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] bg-[#0E7C8F] text-[#F9F8F6]">Attivo</span>}
                    {c.status === "unsubscribed" && <span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] bg-red-200 text-red-800">Disiscritto</span>}
                    {c.status === "bounced" && <span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] bg-[#F4E8D6] text-[#0B192C]">Bounce</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-[#3A4F6B]">{fmtDate(c.imported_at)}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => remove(c.id)}
                      data-testid={`outreach-delete-${c.id}`}
                      className="text-red-700 hover:text-red-900"
                    >
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================================
// CAMPAIGN EDITOR (create draft)
// ============================================================

const DEFAULT_HTML = `<p>Ciao {{first_name}},</p>

<p>ti scrivo per farti conoscere <strong>Metodo di Mare</strong>: un progetto nato da
anni di Marina Militare e di imprenditoria digitale, oggi diventato un libro,
un'associazione e un percorso di formazione.</p>

<p>Sul nostro sito trovi:</p>
<ul>
  <li>📘 Il libro "Metodo di Mare"</li>
  <li>🎓 Corsi gratuiti su leadership digitale, network marketing e tutela online</li>
  <li>🧭 Consulenza 1:1 e percorsi di certificazione</li>
</ul>

<p>Se ti interessa scoprire di più, puoi <a href="https://metododimare.com">visitare il sito</a>
o rispondere direttamente a questa email.</p>

<p>Un saluto cordiale,<br/>
<strong>Michele Trapani</strong><br/>
Metodo di Mare</p>`;

function CampaignEditor({ onBack, onCreated }) {
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState(DEFAULT_HTML);
  const [listLabel, setListLabel] = useState("");
  const [labels, setLabels] = useState([]);
  const [audienceCount, setAudienceCount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/outreach/contacts?limit=1`, { headers: authHdr() });
        setLabels(data.labels || []);
        setAudienceCount(data.stats?.active || 0);
      } catch { /* silent */ }
    })();
  }, []);

  // Recompute audience size when filter label changes
  useEffect(() => {
    (async () => {
      try {
        const params = new URLSearchParams({ status: "active", limit: "1" });
        if (listLabel) params.set("list_label", listLabel);
        const { data } = await axios.get(`${API}/admin/outreach/contacts?${params}`, { headers: authHdr() });
        setAudienceCount(data.total || 0);
      } catch { /* silent */ }
    })();
  }, [listLabel]);

  const create = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const { data } = await axios.post(
        `${API}/admin/outreach/campaigns`,
        { name, subject, html, list_label: listLabel || null },
        { headers: authHdr() }
      );
      onCreated(data.id);
    } catch (err) { setError(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="outreach-campaign-editor" className="max-w-3xl">
      <button onClick={onBack} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#0B192C] hover:text-[#E89B6C] mb-6">
        <ArrowLeft size={13} /> Indietro
      </button>
      <h2 className="font-serif-display text-3xl text-[#0B192C]">Nuova campagna</h2>
      <p className="mt-2 text-sm font-light text-[#3A4F6B]">
        Variabili disponibili nel testo: <code className="font-mono text-[#0E7C8F]">{"{{first_name}}"}</code>, <code className="font-mono text-[#0E7C8F]">{"{{name}}"}</code>, <code className="font-mono text-[#0E7C8F]">{"{{company}}"}</code>. Il link di disiscrizione viene aggiunto automaticamente in fondo.
      </p>

      <form onSubmit={create} className="mt-6 space-y-5 bg-white border border-[#E5E1DA] p-6 md:p-8">
        <div>
          <label className="overline block mb-2">Nome interno campagna *</label>
          <input type="text" required value={name} onChange={(e) => setName(e.target.value)}
            placeholder="es. 'Outreach Marina veterans Q1 2026'"
            data-testid="campaign-name-input"
            className="w-full px-3 py-2 border border-[#0B192C]/20 bg-white text-sm focus:outline-none focus:border-[#0B192C]" />
        </div>
        <div>
          <label className="overline block mb-2">Oggetto email *</label>
          <input type="text" required value={subject} onChange={(e) => setSubject(e.target.value)}
            placeholder="Una rotta per il tuo business digitale 🧭"
            data-testid="campaign-subject-input"
            className="w-full px-3 py-2 border border-[#0B192C]/20 bg-white text-sm focus:outline-none focus:border-[#0B192C]" />
          <p className="text-[10px] text-[#3A4F6B] mt-1">Tip: evita parole spam come &quot;GRATIS&quot; o ALL CAPS</p>
        </div>
        <div>
          <label className="overline block mb-2">Filtra audience (opzionale)</label>
          <select value={listLabel} onChange={(e) => setListLabel(e.target.value)}
            data-testid="campaign-list-select"
            className="w-full px-3 py-2 border border-[#0B192C]/20 bg-white text-sm focus:outline-none focus:border-[#0B192C]">
            <option value="">Tutte le liste ({audienceCount ?? 0} contatti attivi)</option>
            {labels.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
          {audienceCount !== null && (
            <p className="text-[10px] text-[#0E7C8F] mt-1" data-testid="audience-count">
              📨 Verrà inviata a {audienceCount} contatti attivi
            </p>
          )}
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="overline">Corpo email (HTML) *</label>
            <button type="button" onClick={() => setPreview(!preview)}
              data-testid="toggle-preview-btn"
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-[#0E7C8F] hover:text-[#0B192C]">
              <Eye size={11} /> {preview ? "Modifica" : "Anteprima"}
            </button>
          </div>
          {preview ? (
            <div data-testid="campaign-preview"
              className="border border-[#E5E1DA] p-5 bg-[#F9F8F6] text-sm font-light text-[#0B192C] min-h-[280px]"
              dangerouslySetInnerHTML={{ __html: html.replace(/{{first_name}}/g, "Mario").replace(/{{name}}/g, "Mario Rossi").replace(/{{company}}/g, "Acme Srl") }}
            />
          ) : (
            <textarea
              required value={html} onChange={(e) => setHtml(e.target.value)}
              rows={14}
              data-testid="campaign-html-input"
              className="w-full px-3 py-2 border border-[#0B192C]/20 bg-white text-xs font-mono focus:outline-none focus:border-[#0B192C]"
            />
          )}
        </div>
        {error && <p className="text-sm text-red-700">{error}</p>}
        <div className="flex items-center justify-end gap-3">
          <button type="submit" disabled={loading || !audienceCount}
            data-testid="campaign-create-btn"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#0B192C] text-[#F9F8F6] text-xs uppercase tracking-[0.2em] hover:bg-[#E89B6C] hover:text-[#0B192C] disabled:opacity-60">
            {loading ? <><Loader2 size={13} className="animate-spin" /> Creazione...</> : <><Plus size={13} /> Crea bozza</>}
          </button>
        </div>
      </form>
    </div>
  );
}

// ============================================================
// CAMPAIGN DETAIL (preview + test + send)
// ============================================================

function CampaignDetail({ cid, onBack }) {
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [sending, setSending] = useState(false);
  const [info, setInfo] = useState("");
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/outreach/campaigns/${cid}`, { headers: authHdr() });
      setC(data);
    } catch (err) { setError(formatApiError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cid]);

  // Auto-refresh while sending
  useEffect(() => {
    if (c?.status !== "sending") return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
    /* eslint-disable-next-line */
  }, [c?.status]);

  const sendTest = async (e) => {
    e.preventDefault();
    setSendingTest(true); setInfo(""); setError("");
    try {
      await axios.post(`${API}/admin/outreach/campaigns/${cid}/send-test`, { email: testEmail }, { headers: authHdr() });
      setInfo(`Email di test inviata a ${testEmail}. Controlla la casella (anche spam).`);
      load();
    } catch (err) { setError(formatApiError(err)); }
    finally { setSendingTest(false); }
  };

  const sendAll = async () => {
    const confirmMsg = `Sei sicuro di inviare a ${c.audience_count} contatti attivi?\n\nQuesta operazione non è annullabile.\nOggetto: "${c.subject}"`;
    if (!window.confirm(confirmMsg)) return;
    setSending(true); setError("");
    try {
      await axios.post(`${API}/admin/outreach/campaigns/${cid}/send`, {}, { headers: authHdr() });
      setInfo("Invio avviato. Il processo continua in background.");
      load();
    } catch (err) { setError(formatApiError(err)); }
    finally { setSending(false); }
  };

  if (loading) return <div className="flex items-center gap-3 text-[#3A4F6B]"><Loader2 className="animate-spin" size={16} /> Caricamento...</div>;
  if (!c) return <p className="text-sm text-red-700">{error || "Campagna non trovata"}</p>;

  const previewHtml = c.html
    .replace(/{{first_name}}/g, "Mario")
    .replace(/{{name}}/g, "Mario Rossi")
    .replace(/{{company}}/g, "Acme Srl");

  return (
    <div data-testid="campaign-detail" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <button onClick={onBack} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#0B192C] hover:text-[#E89B6C]">
          <ArrowLeft size={13} /> Indietro
        </button>
        <CampaignStatus status={c.status} />
      </div>

      <div className="bg-white border border-[#E5E1DA] p-6 md:p-8">
        <h2 className="font-serif-display text-3xl text-[#0B192C]">{c.name}</h2>
        <p className="mt-2 text-sm font-light text-[#3A4F6B]">Oggetto: <strong className="text-[#0B192C]">{c.subject}</strong></p>
        <div className="mt-4 flex flex-wrap gap-6 text-xs">
          <span>📨 Audience: <strong>{c.audience_count}</strong> contatti</span>
          <span>Lista: <strong>{c.list_label || "tutte"}</strong></span>
          {c.status === "sent" && (
            <>
              <span className="text-[#0E7C8F]">✓ Inviate: <strong>{c.success_count}</strong></span>
              <span className="text-red-700">✗ Fallite: <strong>{c.failed_count}</strong></span>
            </>
          )}
        </div>

        {c.status !== "sent" && (
          <div className="mt-6 grid md:grid-cols-2 gap-4">
            <form onSubmit={sendTest} className="border border-[#E5E1DA] p-4 bg-[#F4E8D6]/30">
              <p className="overline mb-2">Invia email di test</p>
              <div className="flex gap-2">
                <input type="email" required value={testEmail} onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="tu@example.com"
                  data-testid="campaign-test-email-input"
                  className="flex-1 px-3 py-2 border border-[#0B192C]/20 bg-white text-sm font-mono focus:outline-none focus:border-[#0B192C]" />
                <button type="submit" disabled={sendingTest}
                  data-testid="campaign-test-btn"
                  className="inline-flex items-center gap-1 px-3 py-2 border border-[#0E7C8F] text-[#0E7C8F] text-[10px] uppercase tracking-[0.18em] hover:bg-[#0E7C8F] hover:text-white disabled:opacity-60">
                  {sendingTest ? <Loader2 size={11} className="animate-spin" /> : <Mail size={11} />}
                  Test
                </button>
              </div>
            </form>
            <div className="border border-[#E89B6C] p-4 bg-[#E89B6C]/10">
              <p className="overline text-[#E89B6C] mb-2">Invia a tutti</p>
              <p className="text-xs text-[#0B192C] mb-3">{c.audience_count} contatti attivi · rate-limited ~2/sec</p>
              <button onClick={sendAll} disabled={sending || c.status === "sending" || !c.audience_count}
                data-testid="campaign-send-all-btn"
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-[#0B192C] text-[#F9F8F6] text-xs uppercase tracking-[0.18em] hover:bg-[#E89B6C] hover:text-[#0B192C] disabled:opacity-60">
                {sending ? <><Loader2 size={13} className="animate-spin" /> Invio...</> : <><Send size={13} /> Invia a tutti</>}
              </button>
            </div>
          </div>
        )}

        {info && (
          <div data-testid="campaign-info-msg" className="mt-4 flex items-start gap-2 text-sm text-[#0B192C] bg-[#0E7C8F]/10 border border-[#0E7C8F] p-3">
            <CheckCircle2 size={16} className="flex-shrink-0 mt-0.5 text-[#0E7C8F]" />
            <span>{info}</span>
          </div>
        )}
        {error && (
          <div className="mt-4 flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 p-3">
            <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}
      </div>

      <div className="bg-white border border-[#E5E1DA] p-6 md:p-8">
        <p className="overline mb-3">Anteprima email (variabili compilate con dati di esempio)</p>
        <div className="border-l-4 border-[#E89B6C] pl-5 bg-[#F9F8F6] p-5 text-sm font-light text-[#0B192C] leading-relaxed" dangerouslySetInnerHTML={{ __html: previewHtml }} />
      </div>
    </div>
  );
}
